<script setup>
import { useField } from 'vee-validate'
import { onMounted, watch } from 'vue'

const model = defineModel({ type: [Array, String], default: [] })

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: false,
  },
  size: {
    type: String,
    default: 'md',
    validators: value => ['sm', 'md', 'lg'].includes(value),
  },
  direction: {
    type: String,
    default: 'row',
  },
  options: {
    type: Array,
    required: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  classLabel: {
    type: String,
  },
})

const { value, errorMessage } = useField(() => props.name, undefined, {
  syncVModel: true,
})

onMounted(() => {
  if (typeof model.value === 'string') {
    model.value = [model.value]
  }
})

const toggleOption = optionValue => {
  const current = Array.isArray(value.value) ? [...value.value] : []

  if (current.includes(optionValue)) {
    value.value = current.filter(v => v !== optionValue)
  } else {
    value.value = [...current, optionValue]
  }
}

watch(
  model,
  newVal => {
    if (typeof newVal === 'string') {
      model.value = [newVal]
    }
  },
  { immediate: true },
)
</script>
<template>
  <div class="flex flex-col gap-2.5">
    <label
      class="text-neutral-60"
      :for="name"
      :class="{
        'text-base': size === 'lg',
        'text-sm': size === 'md',
        'text-xs': size === 'sm',
      }"
    >
      {{ label }}
    </label>
    <div
      class="flex"
      :class="{
        'flex-col gap-1': direction === 'column',
        'flex-row flex-wrap gap-2': direction === 'row',
      }"
    >
      <div v-for="(item, index) in options" :key="item.id" class="flex flex-row items-center gap-1">
        <label class="cursor-pointer pl-1 text-sm">
          <input
            :id="`multi-check-input${index}`"
            class="text-primary-90 focus:ring-primary-90 cursor-pointer rounded-md disabled:cursor-not-allowed"
            type="checkbox"
            :checked="model.includes(item.value)"
            @change="toggleOption(item.value)"
            :value="item.value"
            :disabled="disabled"
          />
          {{ item.text }}
        </label>
      </div>
    </div>
    <span class="text-error-60 mt-0 pt-0 text-[10px]">{{ errorMessage }}</span>
  </div>
</template>
